// MUST be first: polyfills history.replaceState so Clerk's dev-mode
// setup doesn't throw inside Office's WebView2 task pane iframe.
import "../lib/officeShims";

import { actions } from "./actions";
import type { ExcelAction, LogLevel } from "./actions";
import { attachFeed } from "./feed";
import type { Feed } from "./feed";
import { initAuditUI } from "./audit-ui";
import { initSignInUI } from "./signIn";
import { initInfoUI } from "./info-ui";
import { initSettingsUI } from "./settings-ui";
import { getWorkbookInfo } from "../rules/detection";
import { emitEvent } from "../lib/telemetry";
import { installGlobalErrorHandlers, reportError } from "../lib/errorLog";

let feed: Feed;
let appShellInitialized = false;

Office.onReady(async (info) => {
  if (info.host !== Office.HostType.Excel) return;

  // Install global error capture FIRST so any uncaught failure during
  // init / sign-in / app-shell wiring becomes a reported ERROR_LOGGED row.
  installGlobalErrorHandlers();

  // App-open is the very first thing we record. If sign-in hasn't
  // happened yet the post will 401 silently (telemetry swallows
  // errors), and we'll record the next APP_OPENED after they sign in.
  emitEvent({ event_name: "APP_OPENED" });

  // Gate the entire app shell on sign-in. initSignInUI resolves the FIRST
  // time the user reaches a signed_in state; later sign-outs flip the UI
  // back to the sign-in panel automatically via auth state subscription.
  await initSignInUI();

  if (appShellInitialized) return;
  appShellInitialized = true;

  emitEvent({ event_name: "AUTH_SIGNED_IN" });

  feed = attachFeed(document.getElementById("feed")!);
  document.getElementById("feed-clear")?.addEventListener("click", () => feed.clear());

  // Errors-only filter toggle. State lives on the button's aria-pressed
  // attribute (also drives the active styling); feed.setErrorsOnly
  // applies the data-filter to the list and CSS does the hiding.
  const filterBtn = document.getElementById("feed-filter");
  filterBtn?.addEventListener("click", () => {
    const on = filterBtn.getAttribute("aria-pressed") !== "true";
    filterBtn.setAttribute("aria-pressed", String(on));
    feed.setErrorsOnly(on);
  });
  initTabs();
  renderActions();
  feed.log("Ready.");
  void displayWorkbookInfo();
  const log = (msg: string, level?: LogLevel) => feed.log(msg, level);
  void initAuditUI({ log }).catch((err) => {
    const message = err instanceof Error ? err.message : String(err);
    feed.log(`audit init crashed: ${message}`, "error");
  });
  void initInfoUI({ log }).catch((err) => {
    const message = err instanceof Error ? err.message : String(err);
    feed.log(`info init crashed: ${message}`, "error");
  });
  void initSettingsUI().catch((err) => {
    const message = err instanceof Error ? err.message : String(err);
    feed.log(`settings init crashed: ${message}`, "error");
  });
});

async function displayWorkbookInfo(): Promise<void> {
  const el = document.getElementById("workbook-name");
  try {
    const info = await getWorkbookInfo();
    if (el) {
      el.textContent = info.filename;
      el.title = `Workbook: ${info.filename}\nSheets: ${info.sheetNames.join(", ")}`;
    }
    feed.log(
      `workbook: "${info.filename}" (${info.sheetNames.length} sheet${info.sheetNames.length === 1 ? "" : "s"})`
    );
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (el) el.textContent = "(workbook unavailable)";
    feed.log(`workbook info failed: ${message}`, "error");
  }
}

function initTabs(): void {
  const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>(".tab"));
  const panels = Array.from(document.querySelectorAll<HTMLElement>(".tab-panel"));

  function activate(name: string): void {
    for (const t of tabs) {
      const active = t.dataset.tab === name;
      t.classList.toggle("active", active);
      t.setAttribute("aria-selected", String(active));
    }
    for (const p of panels) {
      p.hidden = p.dataset.tabPanel !== name;
    }
  }

  for (const tab of tabs) {
    tab.addEventListener("click", () => {
      const name = tab.dataset.tab;
      if (name) activate(name);
    });
  }
}

function renderActions(): void {
  const container = document.getElementById("actions");
  if (!container) return;
  container.replaceChildren();

  for (const action of actions) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "action-button";
    button.dataset.actionId = action.id;

    const label = document.createElement("span");
    label.className = "action-label";
    label.textContent = action.label;
    button.appendChild(label);

    if (action.description) {
      const desc = document.createElement("span");
      desc.className = "action-desc";
      desc.textContent = action.description;
      button.appendChild(desc);
    }

    button.addEventListener("click", () => runAction(action, button));
    container.appendChild(button);
  }
}

async function runAction(action: ExcelAction, button: HTMLButtonElement): Promise<void> {
  const log = (message: string, level: LogLevel = "info") => {
    feed.log(`${action.label}: ${message}`, level);
  };

  const startedAt = performance.now();
  emitEvent({ event_name: "ACTION_STARTED", feature_code: action.id });

  button.disabled = true;
  log("started");
  try {
    await action.run({ log });
    emitEvent({
      event_name: "ACTION_COMPLETED",
      feature_code: action.id,
      duration_ms: Math.round(performance.now() - startedAt),
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error(`Action "${action.id}" failed:`, err);
    log(message, "error");
    emitEvent({
      event_name: "ACTION_FAILED",
      feature_code: action.id,
      duration_ms: Math.round(performance.now() - startedAt),
      error_code: "ACTION_THREW",
    });
    reportError({ error_code: "ACTION_THREW", error: err });
  } finally {
    button.disabled = false;
  }
}
